/**
 * Formatting helpers shared by the list and the transcript.
 *
 * Each takes the locale and the translator rather than closing over a fixed
 * language: the interface language is a per-browser preference that changes
 * while the app is open, and a German date stamp under an English interface is
 * exactly the kind of seam a reader notices. Formatters are cached per locale
 * because building an `Intl.DateTimeFormat` is not cheap and the list builds one
 * per row.
 */
import type { Translate } from "./i18n";

type FormatKind = "time" | "weekday" | "dateShort" | "dateFull" | "dayLabel";

const OPTIONS: Record<FormatKind, Intl.DateTimeFormatOptions> = {
  time: { hour: "2-digit", minute: "2-digit" },
  weekday: { weekday: "short" },
  dateShort: { day: "2-digit", month: "2-digit" },
  dateFull: { day: "2-digit", month: "2-digit", year: "numeric" },
  dayLabel: { weekday: "long", day: "numeric", month: "long" },
};

const CACHE = new Map<string, Intl.DateTimeFormat>();

function formatter(locale: string, kind: FormatKind): Intl.DateTimeFormat {
  const cacheKey = `${locale}:${kind}`;
  let found = CACHE.get(cacheKey);
  if (!found) {
    found = new Intl.DateTimeFormat(locale, OPTIONS[kind]);
    CACHE.set(cacheKey, found);
  }
  return found;
}

function startOfDay(value: Date): number {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
}

/** WhatsApp-style relative stamp for the conversation list. */
export function formatListTime(
  timestamp: number | undefined,
  locale: string,
  t: Translate,
): string {
  if (!timestamp) {
    return "";
  }
  const date = new Date(timestamp);
  const today = startOfDay(new Date());
  const day = startOfDay(date);
  const daysAgo = Math.round((today - day) / 86_400_000);

  if (daysAgo <= 0) {
    return formatter(locale, "time").format(date);
  }
  if (daysAgo === 1) {
    return t("time.yesterday");
  }
  if (daysAgo < 7) {
    return formatter(locale, "weekday").format(date);
  }
  if (date.getFullYear() === new Date().getFullYear()) {
    return formatter(locale, "dateShort").format(date);
  }
  return formatter(locale, "dateFull").format(date);
}

export function formatMessageTime(timestamp: number, locale: string): string {
  return timestamp ? formatter(locale, "time").format(new Date(timestamp)) : "";
}

/** Divider label between days in the transcript. */
export function formatDayLabel(timestamp: number, locale: string, t: Translate): string {
  const date = new Date(timestamp);
  const today = startOfDay(new Date());
  const day = startOfDay(date);
  const daysAgo = Math.round((today - day) / 86_400_000);
  if (daysAgo <= 0) {
    return t("time.today");
  }
  if (daysAgo === 1) {
    return t("time.yesterday");
  }
  return formatter(locale, "dayLabel").format(date);
}

export function isSameDay(a: number, b: number): boolean {
  if (!a || !b) {
    return a === b;
  }
  return startOfDay(new Date(a)) === startOfDay(new Date(b));
}

const AVATAR_COLORS = [
  "#e8734a",
  "#4b8dff",
  "#46c68a",
  "#a06bff",
  "#e5a643",
  "#ef5b8c",
  "#39b8c4",
  "#7b8cff",
];

/** Deterministic avatar colour so a conversation keeps its identity across reloads. */
export function avatarColor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

/** One or two initials from a conversation title. */
export function initials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (!words.length) {
    return "?";
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}
